<template>
  <PForm
    ref="pForm"
    :heading="`${formState.edit ? 'Edit' : 'Request for'} Credit Note`"
    :primary-cta="submitButton"
    @close="onClose"
    @reset="onReset"
    @submit.prevent="onSubmit"
  >
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-12">
          <div class="border-none q-ma-none q-pa-none">
            <PField
              v-model="formData.cn_for"
              label="Credit note against?"
              dense
            >
              <template #control>
                <POptionGroup
                  v-model="formData.cn_for"
                  :options="[
                    {
                      description:
                        '(Default) CN to Customer against an Invoice',
                      label: 'Invoice',
                      value: 'invoice',
                    },
                    {
                      description: 'CN to Vendor against a Purchase Bill',
                      label: 'Purchase Bill',
                      value: 'bill',
                    },
                  ]"
                  @update:model-value="onUpdateCNFor"
                />
              </template>
            </PField>
          </div>
        </div>
      </div>
    </div>
    <q-separator spaced="xl" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-3">
          <PSelectInput
            v-if="formData.cn_for === 'bill'"
            v-model="formData.bill_id"
            :options="
              unref(purchaseBillFilter.entities)?.filter((bill) => {
                return bill.status_cd !== 'cancelled' && bill.number;
              })
            "
            :validations="{
              label: 'Purchase Bill',
              required: true,
            }"
            input-class="ellipsis"
            label="Purchase Bill"
            option-label="number"
            option-value="_id"
            placeholder="Select purchase bill"
            :no-options-text="NO_OPTIONS_TEXT.bills"
            @filter="purchaseBillFilter.onFilter"
            @filter-abort="purchaseBillFilter.onFilterAbort"
            @update:model-value="onUpdatePurchaseBill"
          />
          <PSelectInput
            v-else
            v-model="formData.invc_id"
            :options="
              unref(invoiceFilter.entities)?.filter((invoice) => {
                return invoice.status_cd !== 'cancelled' && invoice.number;
              })
            "
            :validations="{
              label: 'Invoice',
              required: true,
            }"
            :no-options-text="NO_OPTIONS_TEXT.invoices"
            input-class="ellipsis"
            label="Invoice"
            option-label="number"
            option-value="_id"
            placeholder="Select invoice"
            @filter="invoiceFilter.onFilter"
            @filter-abort="invoiceFilter.onFilterAbort"
            @update:model-value="onUpdateInvoice"
          />
        </div>
        <div class="col-3">
          <PInput
            v-if="formData.cn_for === 'bill'"
            v-model.trim="formData.bill_number"
            label="Bill No."
            placeholder="Enter value"
            required
            disable
          />
          <PInput
            v-else
            v-model.trim="formData.invc_number"
            label="Invoice No."
            placeholder="Enter value"
            required
            disable
          />
        </div>
        <div class="col-3">
          <PVDateInput
            v-if="formData.cn_for === 'bill'"
            v-model="formData.bill_date"
            label="Bill Date"
            placeholder="DD-MM-YYYY"
            required
            :disable="true"
          />
          <PVDateInput
            v-else
            v-model="formData.invc_date"
            label="Invoice Date"
            placeholder="DD-MM-YYYY"
            required
            :disable="true"
          />
        </div>
        <div class="col-3">
          <PInput
            v-model.trim="formData.vehicle_number"
            label="Vehicle No."
            placeholder="Enter value"
            required
            disable
          />
        </div>
        <div class="col-6 column q-col-gutter-xs">
          <PSelectInput
            v-if="formData.cn_for === 'bill'"
            v-model="unref(formData).billing_address"
            :options="unref(billingAddressFilter.entities)"
            :disable="!unref(formData).bill_id"
            :options-loading="unref(billingAddressFilter.loading)"
            :option-label="
              (val) =>
                getAddressOptionLabel(
                  val,
                  unref(selectedPurchaseBill)?.seller?.gstin,
                )
            "
            label="Bill To"
            required
            placeholder="Select an address"
            @virtual-scroll="billingAddressFilter.onScroll"
            @filter="billingAddressFilter.onFilter"
            @filter-abort="billingAddressFilter.onFilterAbort"
            @update:model-value="
              (value) =>
                updateAddress(
                  value,
                  'billing_address',
                  unref(selectedPurchaseBill)?.seller?.gstin,
                )
            "
          >
            <template #option="{ itemProps, opt }">
              <AddressOption
                :selected="
                  opt._id === unref(formData).billing_address_info?.address_id
                "
                :passed-props="itemProps"
                :address="opt"
                :gstin="unref(selectedPurchaseBill)?.seller?.gstin"
              />
            </template>
          </PSelectInput>
          <PInput
            v-model.trim="temp_billing_address"
            class="col"
            :disable="true"
            :label="formData.cn_for === 'invoice' ? 'Bill To' : ''"
            placeholder="Enter billing address"
            type="textarea"
            autogrow
            required
          />
        </div>
        <div class="col-6 column q-col-gutter-xs">
          <PSelectInput
            v-if="formData.cn_for === 'bill'"
            v-model="unref(formData).shipping_address"
            :disable="!unref(formData).bill_id"
            :options="unref(shippingAddressFilter.entities)"
            :options-loading="unref(shippingAddressFilter.loading)"
            :option-label="
              (val) =>
                getAddressOptionLabel(
                  val,
                  unref(selectedPurchaseBill).seller?.gstin,
                )
            "
            label="Ship To"
            required
            placeholder="Select an address"
            @virtual-scroll="shippingAddressFilter.onScroll"
            @filter="shippingAddressFilter.onFilter"
            @filter-abort="shippingAddressFilter.onFilterAbort"
            @update:model-value="
              (value) =>
                updateAddress(
                  value,
                  'shipping_address',
                  unref(selectedPurchaseBill)?.seller?.gstin,
                )
            "
          >
            <template #option="{ itemProps, opt }">
              <AddressOption
                :selected="
                  opt._id === unref(formData).shipping_address_info?.address_id
                "
                :passed-props="itemProps"
                :address="opt"
                :gstin="unref(selectedPurchaseBill).seller?.gstin"
              />
            </template>
          </PSelectInput>
          <PInput
            v-model.trim="temp_shipping_address"
            :label="formData.cn_for === 'invoice' ? 'Ship To' : ''"
            :disable="true"
            class="col"
            placeholder="Enter shipping address"
            type="textarea"
            autogrow
            required
          />
        </div>
      </div>
    </div>
    <q-separator class="q-mb-xl q-mt-sm" />
    <div class="border-none q-ma-none q-pa-none">
      <PField v-model="formData.type_cd" label="Choose credit note type">
        <template #control>
          <POptionGroup
            v-model="formData.type_cd"
            :options="unref(cnTypeOptions)"
            @update:model-value="onUpdateTypeCode"
          />
        </template>
      </PField>
    </div>
    <div
      v-if="formData.invc_id || formData.bill_id"
      class="overflow-hidden q-mt-lg"
    >
      <legend class="q-mb-sm text-neutral text-weight-medium">Items</legend>

      <q-separator v-if="formData?.lineitems?.length" />

      <PTable
        v-if="formData.lineitems.length"
        :pagination="pagination"
        rows-per-page-label=" "
        :rows-per-page-options="[]"
        :columns="productListColumns"
        :rows="formData.lineitems"
        :style="{ margin: '0 -15px', padding: 0 }"
        table-header-class="text-neutral-70 bg-neutral-10"
        title-class="text-neutral text-md"
        dense
        flat
        wrap-cells
      >
        <template #body-cell="props">
          <q-td :key="props.col.name" :props="props">
            <PEditable
              :model-value="props.value"
              :type="props.col.type"
              disable
            />
          </q-td>
        </template>

        <template #body-cell-name="props">
          <q-td :key="props.col.name" :props="props">
            <ItemNameCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-rate="props">
          <q-td :key="props.col.name" :props="props">
            <ItemRateCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-amount="props">
          <q-td :key="props.col.name" :props="props">
            <ItemAmountCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-qty="props">
          <q-td :key="props.col.name" :props="props">
            <ItemQuantityCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-action="props">
          <q-td :props="props">
            <div class="flex no-wrap justify-center">
              <PBtn
                color="primary"
                dense
                flat
                fluid
                rounded
                title="Edit Item"
                @click="
                  editItem({
                    item: props.row,
                    index: props.rowIndex,
                    entityType: 'Credit Note',
                  })
                "
              >
                <EditIcon class="icon--sm" />
              </PBtn>
              <PBtn dense flat rounded title="Delete Item">
                <RemoveIcon
                  class="icon--sm"
                  @click="removeProduct(props.rowIndex)"
                />
              </PBtn>
            </div>
          </q-td>
        </template>
      </PTable>
      <p v-else class="q-my-xl text-neutral-50">
        <span class="block q-mb-xs">No Items</span>
        <span class="text-error-60">
          <q-icon class="icon--sm" name="sym_r_error_outline" />
          At least one item is required.
        </span>
      </p>
      <div class="row">
        <PBtn
          class="q-mt-md q-px-xs"
          dense
          outline
          @click="
            addItem({ entityType: 'Credit Note', items: entityLineItems })
          "
        >
          <AddIcon class="icon--sm q-mr-sm" />
          <span>Add items to Credit Note</span>
        </PBtn>
      </div>
    </div>
    <q-separator spaced="xl" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-6">
          <PInput
            v-model.trim="formData.remarks"
            label="Remarks (if any)"
            placeholder="Enter remarks"
            type="textarea"
            autogrow
          />
        </div>
        <div v-if="formData.lineitems.length" class="col-6">
          <div class="row q-col-gutter-lg">
            <div class="col-6 ellipsis">
              <p class="q-mb-none">Total {{ formData.type }}</p>
              <span class="text-neutral-70 text-xs"> Excludes Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount
                      ? getTotalAmountWithoutTax()
                      : 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
            <div class="col-6 ellipsis">
              <span class="text-neutral" :title="`Total ${formData.type} Tax`">
                Total {{ formData.type }} Tax
              </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_tax,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div
            v-if="formData.cn_for === 'invoice' && formData.tcs?.code"
            class="row q-mt-lg"
          >
            <div class="col-6 ellipsis">
              <span class="text-neutral" :title="`TCS`">
                TCS ({{ formData.tcs.percentage }}%)
              </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount ? formData.tcs.amount : 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div class="row q-mt-lg">
            <div class="col-6 ellipsis">
              <p class="q-mb-none">Total {{ formData.type }}</p>
              <span class="text-neutral-70 text-xs"> Includes Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <q-separator class="q-mb-xl q-mt-md" />
    <div class="q-mt-sm text-weight-bold">
      {{ FORM_INFO }}
    </div>
  </PForm>
</template>

<script setup>
import AddIcon from "assets/icons/add.svg";
import EditIcon from "assets/icons/edit.svg";
import RemoveIcon from "assets/icons/remove.svg";
import creditNoteRequestFormModel from "src/modules/purchase-order/models/creditNoteRequestForm";
import { useProductList } from "src/modules/purchase-order/composables/product-list";
import { numberFormat } from "assets/scripts/utilities/helpers";
import { FORM_INFO, NO_OPTIONS_TEXT } from "assets/scripts/utilities/constants";
import { index as getAddress } from "src/api/address";

const cnTypeOptions = computed(() => {
  const options = [
    {
      description: "For item's adjusted price",
      label: "Price Adjustment",
      value: "price_adjustment",
    },
  ];

  if (unref(formData).cn_for === "invoice") {
    options.unshift({
      description: "For returned / damaged items",
      label: "Returns",
      value: "returns",
    });

    unref(formData).type = "Returns";

    unref(formData).type_cd = "returns";
  } else {
    options.push({
      description: "For CN to Vendor",
      label: "Purchase Increase",
      value: "increase_purchase",
    });

    unref(formData).type = "Price Adjustment";

    unref(formData).type_cd = "price_adjustment";
  }

  return options;
});

const selectedPurchaseBill = ref({});
const temp_billing_address = ref("");
const temp_shipping_address = ref("");

const onUpdateCNFor = (cn_for) => {
  resetForm();

  clearTempAddresses();

  unref(formData).cn_for = cn_for;
};

const onUpdateTypeCode = (type_cd) => {
  const type = unref(cnTypeOptions).find(
    (option) => option.value === type_cd,
  ).label;

  unref(formData).type = type;

  resetFormLineItems();
};

let billingAddressFilter = {};
let shippingAddressFilter = {};

const updateAddress = (info, address_type, gstin) => {
  if (!info) {
    return;
  }

  unref(formData)[`${address_type}_info`] = {
    city: info.city,
    state: info.state,
    pincode: info.pincode,
    address_line: info.address_line,
    state_code: info.state_code,
    address_id: info._id,
    shop_name: unref(selectedPurchaseBill)?.seller.shop_name,
  };

  const gstinText = gstin ? `\nGSTIN: ${gstin}` : "";
  const concatenatedAddress =
    (info.address_line || "") +
    (info.city ? ", " + info.city : "") +
    (info.state ? ", " + info.state : "") +
    (info.pincode ? ", " + info.pincode : "") +
    (gstinText ? " " + gstinText : "");

  unref(formData)[`${address_type}`] = concatenatedAddress;

  if (address_type == "billing_address") {
    temp_billing_address.value =
      unref(selectedPurchaseBill)?.seller.shop_name +
      "\n" +
      concatenatedAddress;
  } else {
    temp_shipping_address.value =
      unref(selectedPurchaseBill)?.seller.shop_name +
      "\n" +
      concatenatedAddress;
  }
};

const getAddressOptionLabel = (address, gstin) => {
  if (!address) return;
  else if (typeof address === "string") {
    return address;
  } else if (typeof address === "object") {
    const addressLine = address.address_line || address.address;

    return (
      (addressLine || "") +
      (address.city ? ", " + address.city : "") +
      (address.state ? ", " + address.state : "") +
      (address.pincode ? ", " + address.pincode : "") +
      (gstin ? "\nGSTIN: " + gstin : "")
    );
  }
};

const resetFormLineItems = () => {
  unref(formData).lineitems = [];
};

const pForm = ref(null);

const { formState, creditNote } = storeToRefs(useCreditNoteStore());
const { purchaseOrder } = storeToRefs(usePurchaseOrderStore());

let purchaseBillFilter = ref({}),
  invoiceFilter = ref({});

watchEffect(() => {
  purchaseBillFilter.value = useFilter({
    entity: "purchaseBill",
    options: {
      params: {
        po_id: unref(purchaseOrder)._id,
        status_cd: "active",
      },
    },
  });
});

watchEffect(() => {
  invoiceFilter.value = useFilter({
    entity: "invoice",
    options: {
      params: {
        po_id: unref(purchaseOrder)._id,
        status_cd: "active",
        stages:
          "pod_received,pod_accepted,completed,qc_received,qc_accepted,qc_rejected",
      },
    },
  });
});

const { postCreditNote, putCreditNote, toggleCreditNoteRequestForm } =
  useCreditNoteStore();

const { formData, isLoading, closeForm, resetForm, submitForm } = useForm({
  formModel: creditNoteRequestFormModel(),
  entity: creditNote,
  formRef: pForm,
  isForm: formState,
  post: postCreditNote,
  put: putCreditNote,
  toggleForm: toggleCreditNoteRequestForm,
});

const {
  pagination,
  addItem,
  editItem,
  removeProduct,
  getTotalAmountWithoutTax,
} = useProductList({
  formData,
  purchaseOrder,
});

const productListColumns = computed(() => {
  const columns = [
    {
      align: "left",
      name: "name",
      label: "Name",
      field: "name",
    },
    {
      align: "left",
      name: "qty",
      label: "Qty Returned",
      field: "qty",
    },
    {
      align: "right",
      name: "rate",
      label: "Rate",
      field: "rate",
      type: "currency",
    },
    {
      align: "right",
      name: "amount",
      label: "Total",
      field: "amount",
    },
    {
      align: "center",
      classes: "sticky--right",
      headerClasses: "sticky--right",
      name: "action",
      label: "Action",
    },
  ];

  return columns;
});

const populateFormData = async (entity) => {
  if (entity) {
    clearTempAddresses();

    const { vehicle_number } = entity;
    let { buyer, payment_terms } = unref(purchaseOrder);

    formData.value = {
      ...unref(formData),
      vehicle_number,
    };

    if (unref(formData).cn_for === "bill") {
      const { date, number, seller = {} } = entity;
      const addressResponse = await getAddress({
        params: {
          get_primary: true,
          user_id: seller?._id || seller,
        },
      });
      const sellerPrimaryAddress = addressResponse?.data?.data?.[0] || {};

      const address = `${sellerPrimaryAddress.address_line}, ${sellerPrimaryAddress.city}, ${sellerPrimaryAddress.state}, ${sellerPrimaryAddress.pincode}\nGSTIN: ${unref(selectedPurchaseBill)?.seller?.gstin}`;
      const address_info = {
        ...sellerPrimaryAddress,
        address_id: sellerPrimaryAddress._id,
        shop_name: seller.shop_name,
      };

      temp_billing_address.value =
        unref(selectedPurchaseBill)?.seller.shop_name + "\n" + address;

      temp_shipping_address.value =
        unref(selectedPurchaseBill)?.seller.shop_name + "\n" + address;

      formData.value = {
        ...unref(formData),
        bill_date: date,
        bill_id: entity._id,
        bill_number: number,
        po_id: unref(purchaseOrder)._id,
        seller: seller?._id || seller,
        billing_address: address,
        billing_address_info: address_info,
        shipping_address: address,
        shipping_address_info: address_info,
      };
    } else {
      const {
        bill,
        date,
        number,
        po_id,
        pod,
        billing_address: entity_billing_address,
        billing_address_info: entity_billing_address_info,
        shipping_address: entity_shipping_address,
        shipping_address_info: entity_shipping_address_info,
      } = entity;
      const { seller } = bill;
      const { shop_name } = buyer;

      temp_billing_address.value = `${shop_name}\n${entity_billing_address}`;

      temp_shipping_address.value = `${shop_name}\n${entity_shipping_address}`;

      const address_info = {
        ...entity_billing_address_info,
        shop_name: shop_name,
      };
      const shipping_address_info = {
        ...entity_shipping_address_info,
        shop_name: shop_name,
      };

      formData.value = {
        ...unref(formData),
        billing_address: entity_billing_address,
        billing_address_info: address_info,
        invc_date: date,
        invc_id: entity._id,
        invc_number: number,
        payment_terms,
        po_id,
        pod_id: pod?._id || pod,
        seller: seller?._id || seller,
        shipping_address: entity_shipping_address,
        shipping_address_info: shipping_address_info,
        tcs: pod?.tcs,
      };
    }
  }
};

const onSubmit = async () => {
  try {
    await submitForm(false);

    clearTempAddresses();

    closeForm();
  } catch (error) {
    console.error(error);
  }
};

const entityLineItems = ref([]);

const setEntityLineItems = (entity) => {
  entityLineItems.value = entity?.lineitems;

  resetFormLineItems();
};

const onUpdatePurchaseBill = () => {
  selectedPurchaseBill.value = unref(unref(purchaseBillFilter).entities).find(
    (bill) => bill._id === unref(formData).bill_id,
  );

  if (!unref(selectedPurchaseBill)) {
    return;
  }

  billingAddressFilter = useFilter({
    entity: "address",
    options: {
      params: {
        get_primary: true,
        type: "billing",
        user_id: unref(selectedPurchaseBill).seller._id,
      },
    },
  });

  shippingAddressFilter = useFilter({
    entity: "address",
    options: {
      params: {
        get_primary: true,
        type: "shipping",
        user_id: unref(selectedPurchaseBill).seller._id,
      },
    },
  });

  populateFormData(unref(selectedPurchaseBill));

  setEntityLineItems(unref(selectedPurchaseBill));
};

const onUpdateInvoice = (invoice) => {
  invoice = unref(unref(invoiceFilter).entities).find(
    (invoice) => invoice._id === unref(formData).invc_id,
  );

  populateFormData(invoice);

  setEntityLineItems(invoice);
};
const clearTempAddresses = () => {
  temp_billing_address.value = "";

  temp_shipping_address.value = "";
};

const onClose = () => {
  clearTempAddresses();

  closeForm();
};

const onReset = () => {
  resetForm();

  clearTempAddresses();

  setEntityLineItems();
};

const submitButton = computed(() => {
  return {
    loading: unref(isLoading),
    disable: !unref(formData).lineitems.length,
  };
});
</script>
